import { useState } from 'react';
import { useTemplates, useTemplateUpsert, type Template } from '@/api/queries/templates';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Pencil, Plus, Copy, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const SETORES = [
  'saude', 'educacao', 'industria', 'comercio', 'turismo',
  'imobiliario', 'automotivo', 'agro', 'servicos',
  'tecnologia', 'financeiro', 'outro',
];

const ETAPAS = ['msg1', 'msg2', 'msg3', 'objecao_preco', 'objecao_tempo', 'objecao_outro'];

const ETAPA_LABELS: Record<string, string> = {
  msg1: 'msg 1 · abertura',
  msg2: 'msg 2 · follow-up D+4',
  msg3: 'msg 3 · follow-up D+10',
  objecao_preco: 'objeção · preço',
  objecao_tempo: 'objeção · tempo',
  objecao_outro: 'objeção · outro',
};

function TemplateForm({
  template,
  open,
  onOpenChange,
}: {
  template?: Template | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const upsert = useTemplateUpsert();
  const [setor, setSetor] = useState(template?.setor ?? 'outro');
  const [etapa, setEtapa] = useState(template?.etapa ?? 'msg1');
  const [corpo, setCorpo] = useState(template?.corpo ?? '');
  const [ativo, setAtivo] = useState(template?.ativo ?? true);

  async function submit() {
    if (!corpo.trim()) {
      toast.error('Corpo obrigatório');
      return;
    }
    try {
      await upsert.mutateAsync({
        id: template?.id,
        payload: { setor, etapa, corpo: corpo.trim(), ativo },
      });
      toast.success('Template salvo');
      onOpenChange(false);
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {template ? 'Editar template' : 'Novo template'}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground">Setor</label>
              <select
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {SETORES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Etapa</label>
              <select
                value={etapa}
                onChange={(e) => setEtapa(e.target.value)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {ETAPAS.map((e) => <option key={e} value={e}>{ETAPA_LABELS[e]}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Corpo</label>
            <textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              rows={8}
              placeholder="Olá {nome}, vi que sua empresa..."
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm font-mono"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Use variáveis como {'{nome}'}, {'{empresa}'}, {'{cidade}'} pra substituir.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={ativo}
              onChange={(e) => setAtivo(e.target.checked)}
            />
            template ativo
          </label>
          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5 inline -mt-0.5 mr-1" /> cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {upsert.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              salvar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function onClick() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Template copiado');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Falha ao copiar');
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 items-center gap-1 rounded-md border border-border/70 px-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'copiado' : 'copiar'}
    </button>
  );
}

export function TemplatesPage() {
  const { data, isLoading } = useTemplates();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [filterSetor, setFilterSetor] = useState('');
  const [search, setSearch] = useState('');

  const filtered = (data ?? []).filter((t) => {
    if (filterSetor && t.setor !== filterSetor) return false;
    if (search && !t.corpo.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between border-b border-border/70 pb-3">
        <div>
          <h1 className="text-[28px] font-semibold tracking-[-0.02em] leading-tight">Templates</h1>
          <p className="mt-0.5 text-xs text-muted-foreground tabular">
            <span className="mono">{filtered.length}</span> de <span className="mono">{data?.length ?? 0}</span> templates
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          novo template
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="buscar no corpo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
        <select
          value={filterSetor}
          onChange={(e) => setFilterSetor(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2.5 text-sm hover:bg-muted"
        >
          <option value="">todos setores</option>
          {SETORES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum template encontrado.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((t) => (
            <div key={t.id} className="rounded-md border border-border/70 bg-card p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="rounded-full bg-muted px-2 py-0.5 lowercase">{t.setor}</span>
                    <span className="text-muted-foreground lowercase">{ETAPA_LABELS[t.etapa] ?? t.etapa}</span>
                    {!t.ativo && <span className="text-[10px] uppercase text-destructive">inativo</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <CopyButton value={t.corpo} />
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(t);
                      setFormOpen(true);
                    }}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <pre className="text-[12px] whitespace-pre-wrap font-sans text-foreground/85 line-clamp-6">
                {t.corpo}
              </pre>
            </div>
          ))}
        </div>
      )}

      <TemplateForm template={editing} open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
